/**
 * Utilitários centralizados para verificação de horário de funcionamento
 * Versão frontend para Sitri
 * 
 * Bitmask de dias da semana:
 * - 1 = Domingo
 * - 2 = Segunda
 * - 4 = Terça
 * - 8 = Quarta
 * - 16 = Quinta
 * - 32 = Sexta
 * - 64 = Sábado
 */

// Mapeamento de dia da semana (getDay()) para bitmask
export const DIAS_BITMASK = [1, 2, 4, 8, 16, 32, 64]; // DOM, SEG, TER, QUA, QUI, SEX, SAB

export const NOMES_DIAS = {
  1: 'Domingo',
  2: 'Segunda-feira',
  4: 'Terça-feira',
  8: 'Quarta-feira',
  16: 'Quinta-feira',
  32: 'Sexta-feira',
  64: 'Sábado'
};

export const NOMES_DIAS_CURTOS = {
  1: 'Dom',
  2: 'Seg',
  4: 'Ter',
  8: 'Qua',
  16: 'Qui',
  32: 'Sex',
  64: 'Sáb'
};

/**
 * Converte horário string "HH:mm" ou "HH:mm:ss" para minutos desde meia-noite
 * @param {string} horario - Horário no formato "HH:mm" ou "HH:mm:ss"
 * @returns {number} Minutos desde meia-noite
 */
export const horarioParaMinutos = (horario) => {
  if (!horario) return 0;
  const partes = horario.split(':');
  const horas = parseInt(partes[0], 10) || 0;
  const minutos = parseInt(partes[1], 10) || 0;
  return horas * 60 + minutos;
};

/**
 * Obtém o bitmask do dia atual
 * @param {Date} [data] - Data opcional (usa data atual se não fornecida)
 * @returns {number} Bitmask do dia
 */
export const getBitmaskDia = (data = new Date()) => {
  return DIAS_BITMASK[data.getDay()];
};

/**
 * Obtém os minutos atuais desde meia-noite
 * @param {Date} [data] - Data opcional (usa data atual se não fornecida)
 * @returns {number} Minutos desde meia-noite
 */
export const getMinutosAtuais = (data = new Date()) => {
  return data.getHours() * 60 + data.getMinutes();
};

/**
 * Verifica se um horário específico está dentro de um intervalo
 * @param {number} minutosAtuais - Minutos desde meia-noite
 * @param {string} horarioInicial - Horário de abertura "HH:mm"
 * @param {string} horarioFinal - Horário de fechamento "HH:mm"
 * @returns {boolean}
 */
export const estaDentroDoIntervalo = (minutosAtuais, horarioInicial, horarioFinal) => {
  const inicio = horarioParaMinutos(horarioInicial);
  const fim = horarioParaMinutos(horarioFinal);
  
  // Caso normal: abertura antes do fechamento (ex: 08:00 - 22:00)
  if (inicio <= fim) {
    return minutosAtuais >= inicio && minutosAtuais <= fim;
  }
  
  // Caso especial: horário cruza meia-noite (ex: 22:00 - 02:00)
  // Está aberto se: depois da abertura OU antes do fechamento
  return minutosAtuais >= inicio || minutosAtuais <= fim;
};

/**
 * Verifica se está dentro de algum dos turnos
 * @param {number} minutosAtuais - Minutos desde meia-noite
 * @param {Array} turnos - Array de turnos [{horario_inicial, horario_final}]
 * @returns {{dentroTurno: boolean, turnoAtual: object|null, proximoTurno: object|null}}
 */
export const verificarTurnos = (minutosAtuais, turnos) => {
  if (!turnos || turnos.length === 0) {
    return { dentroTurno: false, turnoAtual: null, proximoTurno: null };
  }

  // Ordenar turnos por horário inicial
  const turnosOrdenados = [...turnos].sort((a, b) => 
    horarioParaMinutos(a.horario_inicial) - horarioParaMinutos(b.horario_inicial)
  );

  let turnoAtual = null;
  let proximoTurno = null;

  for (const turno of turnosOrdenados) {
    if (estaDentroDoIntervalo(minutosAtuais, turno.horario_inicial, turno.horario_final)) {
      turnoAtual = turno;
      break;
    }
    
    // Encontrar próximo turno
    const inicioTurno = horarioParaMinutos(turno.horario_inicial);
    if (inicioTurno > minutosAtuais && !proximoTurno) {
      proximoTurno = turno;
    }
  }

  // Se não encontrou próximo turno, pode ser o primeiro do dia
  if (!proximoTurno && turnosOrdenados.length > 0) {
    proximoTurno = turnosOrdenados[0];
  }

  return {
    dentroTurno: turnoAtual !== null,
    turnoAtual,
    proximoTurno
  };
};

/**
 * Verifica se a loja está dentro do horário de funcionamento
 * Suporta múltiplos turnos por dia (múltiplos registros com mesmo dia)
 * 
 * @param {Array} horarios - Array de horários da loja
 * @param {Date} [data] - Data opcional para verificação
 * @returns {{aberta: boolean, horarioHoje: object|null, proximaAbertura: string|null, turnosHoje: Array}}
 */
export const verificarHorarioFuncionamento = (horarios, data = new Date()) => {
  const resultado = {
    aberta: false,
    horarioHoje: null,
    turnosHoje: [],
    proximaAbertura: null,
    motivoFechamento: null,
    turnoAtual: null,
    proximoTurno: null
  };

  const diaAtual = getBitmaskDia(data);
  const minutosAtuais = getMinutosAtuais(data);

  // Coletar todos os turnos do dia (múltiplos registros com mesmo dia)
  const turnosHoje = [];

  if (horarios && Array.isArray(horarios)) {
    // Filtrar todos os horários do dia atual que estão ativos
    // Nota: ativo pode vir como número (0/1) ou booleano
    const horariosDoDia = horarios.filter(h => h.dia === diaAtual && (h.ativo === true || h.ativo === 1));
    
    horariosDoDia.forEach(h => {
      // Evitar duplicatas
      const jaExiste = turnosHoje.some(th => 
        th.horario_inicial === h.horario_inicial && 
        th.horario_final === h.horario_final
      );
      if (!jaExiste) {
        turnosHoje.push({
          ...h,
          turno: turnosHoje.length + 1
        });
      }
    });
  }

  resultado.turnosHoje = turnosHoje;

  // Se não tem horários configurados, considera aberta (comportamento legado)
  if (turnosHoje.length === 0) {
    const temAlgumHorario = horarios && horarios.some(h => h.ativo === true || h.ativo === 1);
    
    if (!temAlgumHorario) {
      resultado.aberta = true;
      return resultado;
    }
    
    resultado.motivoFechamento = 'Fechado hoje';
    resultado.proximaAbertura = calcularProximaAbertura(horarios, data);
    return resultado;
  }

  // Verificar se está dentro de algum turno
  const verificacaoTurnos = verificarTurnos(minutosAtuais, turnosHoje);
  resultado.turnoAtual = verificacaoTurnos.turnoAtual;
  resultado.proximoTurno = verificacaoTurnos.proximoTurno;

  if (verificacaoTurnos.dentroTurno) {
    resultado.aberta = true;
    resultado.horarioHoje = verificacaoTurnos.turnoAtual;
  } else {
    if (verificacaoTurnos.proximoTurno) {
      const inicioProximo = horarioParaMinutos(verificacaoTurnos.proximoTurno.horario_inicial);
      if (inicioProximo > minutosAtuais) {
        resultado.motivoFechamento = `Abre às ${verificacaoTurnos.proximoTurno.horario_inicial.substring(0, 5)}`;
      } else {
        resultado.motivoFechamento = 'Fechado por hoje';
        resultado.proximaAbertura = calcularProximaAbertura(horarios, data);
      }
    } else {
      resultado.motivoFechamento = 'Fechado por hoje';
      resultado.proximaAbertura = calcularProximaAbertura(horarios, data);
    }
  }

  return resultado;
};

/**
 * Calcula a próxima abertura da loja
 * @param {Array} horarios - Array de horários
 * @param {Date} dataAtual - Data atual
 * @returns {string|null} Descrição da próxima abertura
 */
export const calcularProximaAbertura = (horarios, dataAtual = new Date()) => {
  // Verificar próximos 7 dias
  for (let i = 1; i <= 7; i++) {
    const proximaData = new Date(dataAtual);
    proximaData.setDate(proximaData.getDate() + i);
    const diaBitmask = getBitmaskDia(proximaData);
    
    // Coletar turnos do dia (múltiplos registros com mesmo dia)
    const turnosDoDia = [];
    
    if (horarios && Array.isArray(horarios)) {
      const horariosDoDia = horarios.filter(h => h.dia === diaBitmask && (h.ativo === true || h.ativo === 1));
      turnosDoDia.push(...horariosDoDia);
    }
    
    if (turnosDoDia.length > 0) {
      // Pegar o primeiro turno do dia (menor horário inicial)
      const primeiroTurno = turnosDoDia.sort((a, b) => 
        horarioParaMinutos(a.horario_inicial) - horarioParaMinutos(b.horario_inicial)
      )[0];
      
      const nomeDia = NOMES_DIAS[diaBitmask];
      const horario = primeiroTurno.horario_inicial?.substring(0, 5) || '00:00';
      
      if (i === 1) {
        return `Amanhã às ${horario}`;
      }
      return `${nomeDia} às ${horario}`;
    }
  }

  return null;
};

/**
 * Formata o horário de funcionamento para exibição
 * @param {object} horario - Objeto de horário
 * @returns {string} Horário formatado
 */
export const formatarHorario = (horario) => {
  if (!horario || !horario.ativo) return 'Fechado';
  
  const inicio = horario.horario_inicial?.substring(0, 5) || '00:00';
  const fim = horario.horario_final?.substring(0, 5) || '00:00';
  
  return `${inicio} - ${fim}`;
};

/**
 * Formata múltiplos turnos para exibição
 * @param {Array} turnos - Array de turnos
 * @returns {string} Turnos formatados (ex: "11:00 - 15:00 e 19:00 - 23:00")
 */
export const formatarTurnos = (turnos) => {
  if (!turnos || turnos.length === 0) return 'Fechado';
  
  const turnosAtivos = turnos.filter(t => t.ativo || t.b_ativo);
  if (turnosAtivos.length === 0) return 'Fechado';
  
  // Ordenar por horário inicial
  const ordenados = turnosAtivos.sort((a, b) => 
    horarioParaMinutos(a.horario_inicial) - horarioParaMinutos(b.horario_inicial)
  );
  
  return ordenados.map(t => {
    const inicio = t.horario_inicial?.substring(0, 5) || '00:00';
    const fim = t.horario_final?.substring(0, 5) || '00:00';
    return `${inicio} - ${fim}`;
  }).join(' e ');
};

/**
 * Obtém o status completo da loja (horário + fechamento temporário)
 * @param {object} loja - Objeto da loja com horarios e fechamento_temporario
 * @returns {{aberta: boolean, status: string, mensagem: string, cor: string, badge: string}}
 */
export const getStatusLoja = (loja) => {
  // Verificar fechamento temporário primeiro (tem prioridade)
  if (loja?.fechamento_temporario?.ativo) {
    return {
      aberta: false,
      status: 'FECHAMENTO_TEMPORARIO',
      mensagem: loja.fechamento_temporario.motivo || 'Temporariamente fechada',
      cor: 'warning',
      badge: 'TEMPORARIAMENTE FECHADA',
      reaberturaProgramada: loja.fechamento_temporario.reabertura_programada
    };
  }

  // Verificar horário de funcionamento (com suporte a múltiplos turnos)
  const verificacao = verificarHorarioFuncionamento(loja?.horarios);
  
  if (verificacao.aberta) {
    return {
      aberta: true,
      status: 'ABERTA',
      mensagem: 'Aberto agora',
      cor: 'success',
      badge: 'ABERTO',
      horarioHoje: verificacao.horarioHoje,
      turnosHoje: verificacao.turnosHoje,
      turnoAtual: verificacao.turnoAtual
    };
  }

  return {
    aberta: false,
    status: 'FORA_DO_HORARIO',
    mensagem: verificacao.motivoFechamento || 'Fechado',
    cor: 'danger',
    badge: 'FECHADO',
    proximaAbertura: verificacao.proximaAbertura,
    turnosHoje: verificacao.turnosHoje,
    proximoTurno: verificacao.proximoTurno
  };
};

/**
 * Formata data de reabertura para exibição amigável
 * @param {string} dataReabertura - ISO string da data de reabertura
 * @returns {string} Data formatada
 */
export const formatarReabertura = (dataReabertura) => {
  if (!dataReabertura) return '';
  
  try {
    const data = new Date(dataReabertura);
    const agora = new Date();
    const diffMs = data - agora;
    const diffMin = Math.floor(diffMs / 60000);
    
    if (diffMin < 0) return 'Em breve';
    if (diffMin < 60) return `Em ${diffMin} minutos`;
    
    const diffHoras = Math.floor(diffMin / 60);
    if (diffHoras < 24) {
      return `Em ${diffHoras}h${diffMin % 60 > 0 ? ` ${diffMin % 60}min` : ''}`;
    }
    
    // Mais de 24h - mostrar data
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return '';
  }
};

/**
 * Obtém o horário de funcionamento do dia atual formatado
 * @param {Array} horarios - Array de horários
 * @returns {{aberto: boolean, texto: string}}
 */
export const getHorarioHoje = (horarios) => {
  const diaAtual = getBitmaskDia();
  const turnosHoje = [];

  // Coletar turnos do dia (múltiplos registros com mesmo dia)
  if (horarios && Array.isArray(horarios)) {
    const horariosDoDia = horarios.filter(h => h.dia === diaAtual && (h.ativo === true || h.ativo === 1));
    horariosDoDia.forEach(h => {
      const jaExiste = turnosHoje.some(th => 
        th.horario_inicial === h.horario_inicial && 
        th.horario_final === h.horario_final
      );
      if (!jaExiste) {
        turnosHoje.push(h);
      }
    });
  }

  if (turnosHoje.length === 0) {
    // Verificar se tem algum horário configurado
    const temAlgumHorario = horarios && horarios.some(h => h.ativo === true || h.ativo === 1);
    
    if (!temAlgumHorario) {
      return { aberto: true, texto: 'Horário não definido' };
    }
    return { aberto: false, texto: 'Fechado hoje' };
  }

  // Formatar turnos
  const texto = formatarTurnos(turnosHoje);
  return { aberto: true, texto };
};

export default {
  DIAS_BITMASK,
  NOMES_DIAS,
  NOMES_DIAS_CURTOS,
  horarioParaMinutos,
  getBitmaskDia,
  getMinutosAtuais,
  estaDentroDoIntervalo,
  verificarTurnos,
  verificarHorarioFuncionamento,
  calcularProximaAbertura,
  formatarHorario,
  formatarTurnos,
  getStatusLoja,
  formatarReabertura,
  getHorarioHoje
};
